import { connect } from 'react-redux';
import { reset, isSubmitting } from 'redux-form';
import { logEvent } from '@eventbrite/statsig';
import { STATSIG_EVENT_NAMES } from '../../constants/statsig';
import {
    CHECKOUT_STEP_CART,
    RESERVED_TICKETS_PATH,
    TICKETS_SELECTION_PAGE_FORM_NAME,
} from '../../constants';
import {
    getEventTitle,
    getFormattedEventDate,
    getIsDraftEvent,
    getIsReservedSeating,
} from '../../selectors/event';
import { isFormSubmitting } from '../../selectors/forms';
import { getPromoCodeFormProps } from '../../utils/promoCodes';
import {
    getPromoCodeFormActionHandlers,
    promoCodeSubmit as promoCodeSubmitAction,
} from '../../actions/promoCode';
import {
    getCtaText,
    getShouldShowTaxFeesMessage,
    getTotalSelectedTickets,
} from './utils/ticketUtils';
import { trackPromoCodeFormShown } from '../../actions/eventAnalytics';
import {
    ticketQuantityChange as ticketQuantityChangeAction,
    ticketVariantChange as ticketVariantChangeAction,
    updateSelectedPaymentConstraints as updateSelectedPaymentConstraintsAction,
} from '../../actions/tickets';
import {
    notifyParentTicketQuantityChange as notifyParentTicketQuantityChangeAction,
    notifyParentTicketSelectionLayoutStabilized,
} from '../../actions/notifyParent';
import { createOrder, createPickASeatOrder } from '../../actions/order/common';
import { initializeGrylls as initializeGryllsAction } from '../../actions/pixelAnalytics';
import { updateWaitingRoomStep as updateWaitingRoomStepAction } from '../../actions/waitingRoom';
import {
    getFormattedTicketClassesByCategory,
    getReservedTicketSelectionSubmitErrors,
    getShouldShowTicketCategoryGroupHeaders,
    hasSelectedTickets,
} from '../../selectors/tickets';
import {
    getUnformattedOrderCost,
    isEventWithShippingFee,
} from '../../selectors/order';
import { setIsApplePaySupported } from '../../actions/applePay';

const _getFooterProps = (state) => {
    const tickets = state.tickets.ticketsById;
    const totalTicketQuantity = getTotalSelectedTickets(tickets);
    const ctaText = getCtaText(totalTicketQuantity, state.app.isRegEvent);

    return {
        ctaText,
        isSubmitting: isFormSubmitting(state, TICKETS_SELECTION_PAGE_FORM_NAME),
        totalTicketQuantity,
        isDisabled:
            isFormSubmitting(state, TICKETS_SELECTION_PAGE_FORM_NAME) ||
            !hasSelectedTickets(state),
    };
};

const _mapStateToProps = (state) => {
    const shouldShowTaxAndFeesMessage = getShouldShowTaxFeesMessage(
        state.tickets.ticketsById,
    );
    const formattedTicketClasses = getFormattedTicketClassesByCategory(state);

    // UX consideration: show the ticket class group titles only if more than one of the ticket
    // class collections has values.
    const shouldShowTicketClassTitles =
        getShouldShowTicketCategoryGroupHeaders(state);

    return {
        currencyFormat: state.app.currencyFormat,
        eventSeatMapThumbnailSrc: state.event.properties.seatmapThumbnailUrl,
        isModal: state.app.isModal,
        locale: state.app.locale,
        shouldShowWaitingRoomCTA: state.waitingRoom.isWaitingRoomShown,
        showPickASeatButton: state.event.showPickASeat,
        shouldShowTaxAndFeesMessage,
        submitErrors: getReservedTicketSelectionSubmitErrors(state),
        // The ticket data for a reserved seating event quacks much like a Ticket Class but it is in
        // fact a Ticket Rule.
        ticketRulesByCategory: formattedTicketClasses,
        shouldShowTicketClassTitles,
        eventTitle: getEventTitle(state),
        eventDate: getFormattedEventDate(state),
        isReservedSeating: getIsReservedSeating(state),
        isDraftEvent: getIsDraftEvent(state),
        isFormSubmitting: isSubmitting(TICKETS_SELECTION_PAGE_FORM_NAME)(state),
        ..._getFooterProps(state),
        ...getPromoCodeFormProps(state),
        unformattedOrderCost: getUnformattedOrderCost(state),
        isEventWithShippingFee: isEventWithShippingFee(state),
        urgencySignals: state.app.urgencySignals,
    };
};

export const _mapDispatchToProps = (dispatch) => ({
    onPageWillMount: (shouldShowPromoCodeForm) => {
        dispatch(initializeGryllsAction());
        dispatch(updateWaitingRoomStepAction(CHECKOUT_STEP_CART));
        dispatch(setIsApplePaySupported());
        // Temporarily recording an action to know when the promo code
        // is shown in the checkoutWidget https://jira.evbhome.com/browse/EB-126752
        if (shouldShowPromoCodeForm) {
            dispatch(trackPromoCodeFormShown(RESERVED_TICKETS_PATH));
        }
        logEvent(STATSIG_EVENT_NAMES.TICKET_STEP_PAGE_VIEW);
        dispatch(notifyParentTicketSelectionLayoutStabilized());
    },
    createPickASeatOrder: async () => {
        await dispatch(createPickASeatOrder());

        dispatch(reset(TICKETS_SELECTION_PAGE_FORM_NAME));
    },
    onQuantityChange: (data) => {
        dispatch(ticketQuantityChangeAction(data, RESERVED_TICKETS_PATH));
        dispatch(updateSelectedPaymentConstraintsAction());
        dispatch(notifyParentTicketQuantityChangeAction(data));
    },
    onVariantChange: (data) => {
        dispatch(ticketVariantChangeAction(data));
    },
    // Important: Always use a return statement or arrow function with concise body syntax. Redux
    // Form's onSubmit handler requires you to return a promise in order to properly update form
    // state. As relates to server-side validation:
    // https://redux-form.com/6.0.0-rc.1/examples/submitvalidation/
    onSubmit: async () => {
        await dispatch(createOrder({ shouldRedirect: true }));

        dispatch(reset(TICKETS_SELECTION_PAGE_FORM_NAME));
    },
    promoCodeFormHandlers: {
        ...getPromoCodeFormActionHandlers(dispatch),
        onSubmit: (data) => {
            dispatch(promoCodeSubmitAction(data, RESERVED_TICKETS_PATH))
                // Do nothing, errors are caught in promoCodeSubmitAction
                .catch(() => null);
        },
    },
});

export const connectReservedTicketSelectionPage = (ComponentClass) =>
    connect(_mapStateToProps, _mapDispatchToProps)(ComponentClass);
